import { handleError } from '@anchorage/sentry/errors';
import { sumBy } from 'lodash-es';

type Asset = Partial<{
  assetTypeInfo: Partial<{
    assetTypeID: string;
    value: string;
  }>;
  completedBalance: string;
}>;

// Subtype means you can pass in an object that has all of these properties
// and more
type Vault = Partial<{
  assets: Array<Asset>;
  vaultID: Partial<{ vaultSubID: string }>;
}>;

type VaultSumArgs = {
  excludeAssetTypeId?: string;
  excludeVaultSubId?: string;
  onlyAssetTypeId?: string;
  onlyVaultSubId?: string;
  vaults: Array<Vault>;
};

// Internal function used to sum all assets within all vaults
const _sumAssetsInVaults = ({
  excludeAssetTypeId,
  excludeVaultSubId,
  onlyAssetTypeId,
  onlyVaultSubId,
  vaults,
  includeValue,
}: VaultSumArgs & {
  includeValue: boolean;
}): number => {
  if (!vaults) {
    return 0;
  }

  // Sum all of the assets in all of the vaults
  return sumBy(vaults, (vault: Vault) => {
    const thisVaultSubId = vault.vaultID && vault.vaultID.vaultSubID;

    // If we're using only this vaultSubId and it's not a match, return 0 (skip)
    if (onlyVaultSubId && thisVaultSubId !== onlyVaultSubId) {
      return 0;
    }

    // If we're excluding this vaultSubId and it is a match, return 0 (skip)
    if (excludeVaultSubId && excludeVaultSubId === thisVaultSubId) {
      return 0;
    }

    // Sum all of the assets in this vault
    return sumBy(vault.assets, (asset: Asset) => {
      const thisAssetId =
        asset.assetTypeInfo && asset.assetTypeInfo.assetTypeID;

      // If we're using only this assetTypeId and it's not a match,
      // return 0 (skip)
      if (onlyAssetTypeId && onlyAssetTypeId !== thisAssetId) {
        return 0;
      }

      // If we're excluding this assetTypeId and it is a match, return 0 (skip)
      if (excludeAssetTypeId && excludeAssetTypeId === thisAssetId) {
        return 0;
      }

      return includeValue
        ? // Object is possibly 'undefined'.
          // @ts-expect-error - TODO TS
          +asset.completedBalance * +asset.assetTypeInfo.value
        : // Object is possibly 'undefined'.
          // @ts-expect-error - TODO TS
          +asset.completedBalance;
    });
  });
};

// Return the sum (as a number) of assets within vaults, as filtered by a given
// asset type ID or vault sub ID.
// NOTE: This should be used only with a single asset type as summing assets
// of different types is invalid. Ex: cannot add 1 ETH + 2 BTC. For this use,
// compare the USD value
export const sumAssetsInVaults = (vaultSumArgs: VaultSumArgs): number => {
  if (typeof vaultSumArgs.onlyAssetTypeId === 'undefined') {
    handleError(
      new Error(
        'Invalid usage of sumAssetsInVaults. Please define `onlyAssetTypeId` or use sumAssetsInVaultsUSD',
      ),
    );
  }

  return _sumAssetsInVaults({ ...vaultSumArgs, includeValue: false });
};

// Return the sum (as a number) of the USD value of all assets within all vaults
// as filtered by a given asset type ID or vault sub ID.
export const sumAssetsInVaultsUSD = (vaultSumArgs: VaultSumArgs): number => {
  return _sumAssetsInVaults({ ...vaultSumArgs, includeValue: true });
};
